import { useQuery, useQueryClient } from 'react-query';

import { type AccountingSoftware } from 'modules/bookkeep/integration/status';
import { getV2ApiCompanyIdAccountingIntegrationsAccountingSoftware } from 'src/client/spendesk-api';
import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { useCompanyId } from '../../app/hooks/useCompanyId';

type Integration = AccountingSoftware | 'noIntegration';

export const useAccountingSoftwareDisplayName = () => {
  const { t } = useTranslation('global');
  const accountingSoftwareNameMap: Record<Integration, string> = {
    Netsuite: 'NetSuite',
    Quickbooks: 'QuickBooks Online',
    Datev: 'DATEV',
    DatevRewe: 'DATEV ReWe',
    MicrosoftDynamics: 'Microsoft Business Central',
    Sage100: 'Sage 100',
    ExactOnlineBe: 'Exact Online BE',
    ExactOnlineFr: 'Exact Online FR',
    ExactOnlineNl: 'Exact Online NL',
    SpendeskAccounting: t('integrations.fullNames.doubleEntry'),
    SpendeskAccountingSingleEntry: t('integrations.fullNames.singleEntry'),
    ACD: 'ACD',
    Cegid: 'Cegid',
    Odoo: 'Odoo',
    PublicApi: 'API',
    Sage: 'Sage',
    SageIntacct: 'Sage Intacct',
    Xero: 'Xero',
    noIntegration: t('bookkeep.integrations.settings.baseSwitch.noIntegration'),
  };

  const getDisplayName = (integration: Integration | undefined) => {
    if (!integration) {
      return integration;
    }
    return accountingSoftwareNameMap[integration] || integration;
  };

  return { getDisplayName };
};

export const useAccountingSoftwareName = () => {
  const companyId = useCompanyId();
  const { getDisplayName } = useAccountingSoftwareDisplayName();

  const { data, isLoading } = useQuery({
    queryKey: ['accounting-software', companyId],
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async () => {
      const response =
        await getV2ApiCompanyIdAccountingIntegrationsAccountingSoftware({
          path: {
            companyId,
          },
        });

      if (response.statusCode === 200 && response.body.status === 'connected') {
        return response.body.accountingSoftware;
      }

      return undefined;
    },
  });

  const accountingSoftwareName = getDisplayName(data);

  return {
    integration: data,
    accountingSoftwareName,
    isLoading,
  };
};

export const useInvalidateAccountingSoftwareQuery = () => {
  const companyId = useCompanyId();
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({
      queryKey: ['accounting-software', companyId],
    });
  };
};
